import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { requireAuthenticatedUser } from '../../decorators'
import { updateProductInSupplierProducts } from '../supplierProducts/mockData'
import {
  getProductData,
  getKitProductListData,
  getItemProductListData,
  getProductListData,
  getProductOverviewData,
  getSuppliersForProductData,
} from './mockData'
import type { IItemProduct, TProduct } from '~/types/product/models'
import { GetListResponse } from '~/api/types'
import { getProductKitItemsData } from '~/api/msw/api/productKitItems/mockData'

interface GetProductsResponse {
  data: TProduct[]
}

async function getProducts(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetProductsResponse = {
    data: await getProductListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

interface GetKitProductsResponse {
  data: TProduct[] | undefined
}

async function getKitProducts(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetKitProductsResponse = {
    data: await getKitProductListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

interface GetItemProductsResponse {
  data: TProduct[] | undefined
}

async function getItemProducts(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetItemProductsResponse = {
    data: await getKitProductListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getProduct(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const obj = await getProductData(id)
  if (obj) {
    const responseBody = { data: obj }
    return res(ctx.status(200), ctx.json(responseBody))
  } else {
    const responseBody = { statusCode: 404, message: 'Resource not found.' }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

async function getProductOverview(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const obj = await getProductOverviewData(id)

  if (obj) {
    const responseBody = { data: obj }
    return res(ctx.status(200), ctx.json(responseBody))
  } else {
    const responseBody = { statusCode: 404, message: 'Resource not found.' }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

// We're now using the getProductOveriew API call to get
// kit items for a product. Leaving this here for a little
// while till we're sure that was a good choice.  JR.
//
// async function getKitItemsForProduct(
//   req: RestRequest,
//   res: ResponseFunction,
//   ctx: RestContext
// ) {
//   const { id } = req.params
//   if (typeof id !== 'string') {
//     const responseBody = {
//       statusCode: 500,
//       message: 'Passed ID is not a string.',
//     }
//     return res(ctx.status(500), ctx.json(responseBody))
//   }
//   const obj = { data: await getProductKitItemsData(id) }

//   if (obj) {
//     const responseBody = obj
//     return res(ctx.status(200), ctx.json(responseBody))
//   } else {
//     const responseBody = { statusCode: 404, message: 'Resource not found.' }
//     return res(ctx.status(404), ctx.json(responseBody))
//   }
// }

async function getSuppliersForProductById(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const wellFormedSupplierProductsOnly =
    req.url.searchParams.get('wellformedsupplierproductsonly') === 'true'

  console.log(
    `Returning suppliers for product '${id}',` +
      ` wellFormedSupplierProductsOnly = ${wellFormedSupplierProductsOnly}.`
  )
  const responseBody = {
    data: await getSuppliersForProductData(id, wellFormedSupplierProductsOnly),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

// post

const postProduct = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getProductListData)
}

const patchProduct = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getProductListData, [
    updateProductInSupplierProducts,
  ])
}

const deleteProduct = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getProductListData)
}

export const handlers = [
  {
    method: 'get',
    path: '/api/products',
    handler: getProducts,
  },
  {
    method: 'get',
    path: '/api/products/kit',
    handler: getKitProducts,
  },
  {
    method: 'get',
    path: '/api/products/item',
    handler: getItemProducts,
  },
  {
    method: 'get',
    path: '/api/products/:id',
    handler: getProduct,
  },
  {
    method: 'get',
    path: '/api/products/:id/overview',
    handler: getProductOverview,
  },
  // We're now using the getProductOveriew API call to get
  // kit items for a product. Leaving this here for a little
  // while till we're sure that was a good choice.  JR.
  //
  // {
  //   method: 'get',
  //   path: '/api/products/:id/kititems',
  //   handler: getKitItemsForProduct,
  // },
  {
    method: 'get',
    path: '/api/products/:id/suppliers',
    handler: getSuppliersForProductById,
  },
  {
    method: 'post',
    path: '/api/products',
    handler: postProduct,
  },
  {
    method: 'patch',
    path: '/api/products/:id',
    handler: patchProduct,
  },
  {
    method: 'delete',
    path: '/api/products/:id',
    handler: deleteProduct,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
